import { createSelector } from 'reselect';
// Import enumerations.
import {
	NewBusinessApplicationSectionName,
	RecordStatus,
	SectionStatus,
} from '@app/enumerations';
// Import state types.
import {
	AppState,
} from '@app/redux/states';
// Import props.
import {
	OptionalSectionKeyProps,
	SectionProps,
} from '@app/props';
// Import api models.
import {
	BuildingResponseCommercialExistingWithStatus,
	NewBusinessApplication,
	NewBusinessApplicationSection,
	Section,
	SectionStatusInfo,
} from '@app/models/api';
// Import selectors.
import {
	makeGetNewBusinessBuildingsCommercialSelector,
	makeGetNewBusinessEligibilityErrorsSelector,
	makeGetNewBusinessApplicationSelector,
	makeIsNewBusinessApplicationSubmittedOrHasUnderwritingDecisionSelector,
} from '@app/redux/selectors';
// Import base selectors.
import {
	getNewBusinessApplicationState,
} from '@app/redux/selectors/base';


// Fetches the new business application section name.
const getNewBusinessApplicationSectionName = (state: AppState, props: SectionProps<NewBusinessApplicationSectionName>): NewBusinessApplicationSectionName => {
	return props.sectionName;
}

// Fetches the optional section key prop.
const getOptionalNewBusinessApplicationSectionKey = (state: AppState, props: OptionalSectionKeyProps): string | undefined => {
	return props.sectionKey;
}

// Get New Business Application Sections Selector.
const makeGetNewBusinessApplicationSectionsSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector(), makeGetNewBusinessBuildingsCommercialSelector(), makeIsNewBusinessApplicationSubmittedOrHasUnderwritingDecisionSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined, buildings: BuildingResponseCommercialExistingWithStatus[], isNewBusinessApplicationSubmittedOrHasUnderwritingDecision: boolean): NewBusinessApplicationSection[] => {
		return newBusinessApplication !== undefined
			&& newBusinessApplication.status !== null
			? newBusinessApplication.status.sections.filter((section: Section<NewBusinessApplicationSectionName>) => {
				return section.name !== NewBusinessApplicationSectionName.BUILDING;
			}).map((section: Section<NewBusinessApplicationSectionName>) => {
				// Convert the sections to new business application sections.
				let sectionCopy: NewBusinessApplicationSection = new NewBusinessApplicationSection();
				sectionCopy.friendlyName = section.friendlyName;
				sectionCopy.name = section.name;
				sectionCopy.status = section.status !== null ? { ...section.status } : new SectionStatusInfo();
				sectionCopy.isEditable = section.isEditable;
				sectionCopy.unsubmitNewBusinessApplicationOnChange = false;

				switch (sectionCopy.name) {		
					case NewBusinessApplicationSectionName.BUILDINGS:
						// Override the editable flag to true if the new business application is submitted.
						sectionCopy.isEditable = isNewBusinessApplicationSubmittedOrHasUnderwritingDecision && !sectionCopy.isEditable ? true : sectionCopy.isEditable;
						// Mark the section to unsubmit the new business application if changes are made.
						sectionCopy.unsubmitNewBusinessApplicationOnChange = isNewBusinessApplicationSubmittedOrHasUnderwritingDecision;
						// Convert all the buildings into sub sections for the buildings section.
						sectionCopy.subSections = buildings.map((building: BuildingResponseCommercialExistingWithStatus, index: number) => {
							// Set up section.
							let buildingSection: NewBusinessApplicationSection = new NewBusinessApplicationSection();
							buildingSection.friendlyName = building.buildingData !== null && building.buildingData.buildingDescription !== null ? building.buildingData?.buildingDescription : `Building ${index}`;
							buildingSection.isEditable = sectionCopy.isEditable;
							buildingSection.unsubmitNewBusinessApplicationOnChange = sectionCopy.unsubmitNewBusinessApplicationOnChange;
							buildingSection.key = building.id;
							buildingSection.name = NewBusinessApplicationSectionName.BUILDING;
							// Set up section status info.
							let sectionStatusInfo: SectionStatusInfo = new SectionStatusInfo();
							sectionStatusInfo.statusCode = building.status?.statusCode === RecordStatus.COMPLETE ? SectionStatus.COMPLETE : SectionStatus.REQUIRED;
							sectionStatusInfo.statusMessage = building.status?.statusMessage ?? sectionStatusInfo.statusCode;

							buildingSection.status = sectionStatusInfo;
							return buildingSection;
						});
						break;
					case NewBusinessApplicationSectionName.BUILDING:
					case NewBusinessApplicationSectionName.COVERAGE_LIMITS:
					case NewBusinessApplicationSectionName.DWELLING:
					case NewBusinessApplicationSectionName.PERILS:
					case NewBusinessApplicationSectionName.PROPERTY_ADDRESS:
					case NewBusinessApplicationSectionName.APPETITE_AND_ELIGIBILITY:
						// Override the editable flag to true if the new business application is submitted.
						sectionCopy.isEditable = isNewBusinessApplicationSubmittedOrHasUnderwritingDecision && !sectionCopy.isEditable ? true : sectionCopy.isEditable;
						// Mark the section to unsubmit the new business application if changes are made.
						sectionCopy.unsubmitNewBusinessApplicationOnChange = isNewBusinessApplicationSubmittedOrHasUnderwritingDecision;
						break;
					case NewBusinessApplicationSectionName.PAY_AND_ISSUE_POLICY:
						// Make sure that the pay and issue policy section is always accessible.
						if (sectionCopy.status !== null
							&& sectionCopy.status.statusCode === SectionStatus.NOT_ELIGIBLE) {
							sectionCopy.status.statusCode = SectionStatus.REQUIRED;
						}
						break;
				}

				return sectionCopy;
			})
			: [];
	}
);

// Get Flattened New Business Application Sections Selector.
const makeGetFlattenedNewBusinessApplicationSectionsSelector = () => createSelector(
	[makeGetNewBusinessApplicationSectionsSelector()],
	(newBusinessApplicationSections: NewBusinessApplicationSection[]): NewBusinessApplicationSection[] => {
		return newBusinessApplicationSections.flatMap((newBusinessApplicationSection: NewBusinessApplicationSection) => {
			return newBusinessApplicationSection.subSections.length > 0 ? [newBusinessApplicationSection].concat(newBusinessApplicationSection.subSections) : [newBusinessApplicationSection]
		});
	}
);

// Get New Business Application Progress Percentage Selector.
const makeGetNewBusinessApplicationProgressPercentageSelector = () => createSelector(
	[makeGetFlattenedNewBusinessApplicationSectionsSelector()],
	(flattenedNewBusinessApplicationSections: NewBusinessApplicationSection[]): number => {
		// Filter out the sections that will exclude the 'pay and issue policy' section, and sections that have sub sections.
		let newBusinessApplicationSectionsToBeCounted: NewBusinessApplicationSection[] = flattenedNewBusinessApplicationSections.filter((section: NewBusinessApplicationSection) => {
			return section.name !== NewBusinessApplicationSectionName.PAY_AND_ISSUE_POLICY
				&& section.subSections.length === 0;
		});
		// Get total count that has been completed.
		let completedCount: number = newBusinessApplicationSectionsToBeCounted.filter((section: NewBusinessApplicationSection) => {
			return section.status !== null
				&& section.status.statusCode === SectionStatus.COMPLETE;
		}).length;
		// Get total count of sections.
		let totalCount: number = newBusinessApplicationSectionsToBeCounted.length;
		// Calculate progress percentage of all new business application sections.
		let progressPercentage: number = (completedCount / totalCount) * 100;

		return parseInt(progressPercentage.toString());
	}
);

// Are All New Business Application Sections Complete Selector.
const makeAreAllNewBusinessApplicationSectionsCompleteSelector = () => createSelector(
	[makeGetNewBusinessApplicationProgressPercentageSelector()],
	(progressPercentage: number): boolean => {
		return progressPercentage === 100;
	}
);

// Get Default New Business Application Section Selector.
const makeGetDefaultNewBusinessApplicationSectionSelector = () => createSelector(
	[makeGetNewBusinessApplicationSectionsSelector()],
	(newBusinessApplicationSections: NewBusinessApplicationSection[]): NewBusinessApplicationSection | undefined => {
		return newBusinessApplicationSections.find((newBusinessApplicationSection: NewBusinessApplicationSection) => {
			return newBusinessApplicationSection.name === NewBusinessApplicationSectionName.PAY_AND_ISSUE_POLICY;
		});
	}
);

// Get Default New Business Application Section Name Selector.
const makeGetDefaultNewBusinessApplicationSectionNameSelector = () => createSelector(
	[makeGetDefaultNewBusinessApplicationSectionSelector()],
	(defaultNewBusinessApplicationSection: NewBusinessApplicationSection | undefined): NewBusinessApplicationSectionName | undefined => {
		return defaultNewBusinessApplicationSection?.name ?? undefined;
	}
);

// Get New Business Application Section Selector.
const makeGetNewBusinessApplicationSectionSelector = () => createSelector(
	[getNewBusinessApplicationSectionName, getOptionalNewBusinessApplicationSectionKey, makeGetFlattenedNewBusinessApplicationSectionsSelector()],
	(sectionName: NewBusinessApplicationSectionName, sectionKey: string | undefined, flattenedNewBusinessApplicationSections: NewBusinessApplicationSection[]): NewBusinessApplicationSection | undefined => {
		if (sectionKey !== undefined) {
			return flattenedNewBusinessApplicationSections.find((section: NewBusinessApplicationSection) => section.name === sectionName && (section.key === sectionKey || section.key === null));
		}
		return flattenedNewBusinessApplicationSections.find((section: NewBusinessApplicationSection) => section.name === sectionName);
	}
);

// Does New Business Application Section Exist Selector.
const makeDoesNewBusinessApplicationSectionExistSelector = () => createSelector(
	[makeGetNewBusinessApplicationSectionSelector()],
	(newBusinessApplicationSection: NewBusinessApplicationSection | undefined): boolean => {
		return newBusinessApplicationSection !== undefined;
	}
);

// Is New Business Application Section Required Selector.
const makeIsNewBusinessApplicationSectionRequiredSelector = () => createSelector(
	[makeGetNewBusinessApplicationSectionSelector()],
	(newBusinessApplicationSection: NewBusinessApplicationSection | undefined): boolean => {
		return newBusinessApplicationSection !== undefined
			? newBusinessApplicationSection.status?.statusCode === SectionStatus.REQUIRED
			: false;
	}
);

// Is New Business Application Section Eligible Selector.
const makeIsNewBusinessApplicationSectionEligibleSelector = () => createSelector(
	[makeGetNewBusinessApplicationSectionSelector()],
	(newBusinessApplicationSection: NewBusinessApplicationSection | undefined): boolean => {
		return newBusinessApplicationSection !== undefined
			? newBusinessApplicationSection.status?.statusCode !== SectionStatus.NOT_ELIGIBLE
			: false;
	}
);

// Is New Business Application Section Complete Selector.
const makeIsNewBusinessApplicationSectionCompleteSelector = () => createSelector(
	[makeGetNewBusinessApplicationSectionSelector()],
	(newBusinessApplicationSection: NewBusinessApplicationSection | undefined): boolean => {
		return newBusinessApplicationSection !== undefined
			? newBusinessApplicationSection.status?.statusCode === SectionStatus.COMPLETE
			: false;
	}
);

// Is New Business Application Section Accessible Selector.
const makeIsNewBusinessApplicationSectionAccessibleSelector = () => createSelector(
	[makeGetNewBusinessApplicationSectionSelector()],
	(newBusinessApplicationSection: NewBusinessApplicationSection | undefined): boolean => {
		let isNewBusinessApplicationSectionAccessible: boolean = false;
		if (newBusinessApplicationSection !== undefined) {
			switch (newBusinessApplicationSection.status?.statusCode) {
				case SectionStatus.COMPLETE:
				case SectionStatus.NOT_REQUIRED:
				case SectionStatus.REQUIRED:
				case SectionStatus.IN_THE_WORKS:
					isNewBusinessApplicationSectionAccessible = true;
					break;
				case SectionStatus.NOT_ELIGIBLE:
				case SectionStatus.UNKNOWN:
				default:
					isNewBusinessApplicationSectionAccessible = false;
					break;
			}
		}

		return isNewBusinessApplicationSectionAccessible;
	}
);

// Get New Business Application Section Label Selector.
const makeGetNewBusinessApplicationSectionLabelSelector = () => createSelector(
	[makeGetNewBusinessApplicationSectionSelector()],
	(newBusinessApplicationSection: NewBusinessApplicationSection | undefined): string | undefined => {
		return newBusinessApplicationSection !== undefined
			&& newBusinessApplicationSection.friendlyName !== null
			? newBusinessApplicationSection.friendlyName
			: undefined;
	}
);

// Get New Business Application Section Status Message Selector.
const makeGetNewBusinessApplicationSectionStatusMessageSelector = () => createSelector(
	[makeGetNewBusinessApplicationSectionSelector()],
	(newBusinessApplicationSection: NewBusinessApplicationSection | undefined): string | undefined => {
		return newBusinessApplicationSection !== undefined
			&& newBusinessApplicationSection.status !== null
			&& newBusinessApplicationSection.status.statusMessage !== null
			&& !String.isNullOrWhiteSpace(newBusinessApplicationSection.status.statusMessage)
			? newBusinessApplicationSection.status.statusMessage
			: undefined;
	}
);

// Get New Business Application Section Non-Eligibility Status Message Selector.
const makeGetNewBusinessApplicationSectionNonEligibilityStatusMessageSelector = () => createSelector(
	[makeGetNewBusinessApplicationSectionStatusMessageSelector(), makeGetNewBusinessEligibilityErrorsSelector()],
	(sectionStatusMessage: string | undefined, eligibilityErrors: string[]): string | undefined => {
		return sectionStatusMessage !== undefined
			&& eligibilityErrors.find((eligibilityError: string) => eligibilityError === sectionStatusMessage) === undefined
			? sectionStatusMessage
			: undefined;
	}
);

// Is New Business Application Section Editable Selector.
const makeIsNewBusinessApplicationSectionEditableSelector = () => createSelector(
	[makeGetNewBusinessApplicationSectionSelector()],
	(newBusinessApplicationSection: NewBusinessApplicationSection | undefined): boolean => {
		return newBusinessApplicationSection !== undefined
			&& newBusinessApplicationSection.isEditable !== null
			&& newBusinessApplicationSection.isEditable
	}
);

// Require Unsubmission On Edits For New Business Application Section Selector.
const makeRequireUnsubmissionOnEditsForNewBusinessApplicationSectionSelector = () => createSelector(
	[makeGetNewBusinessApplicationSectionSelector()],
	(newBusinessApplicationSection: NewBusinessApplicationSection | undefined): boolean => {
		return newBusinessApplicationSection !== undefined
			&& newBusinessApplicationSection.unsubmitNewBusinessApplicationOnChange !== null
			? newBusinessApplicationSection.unsubmitNewBusinessApplicationOnChange
			: false;
	}
);


// Export composed methods.
export {
	getNewBusinessApplicationState,
};

// export selectors.
export {
	makeGetNewBusinessApplicationSectionsSelector,
	makeGetFlattenedNewBusinessApplicationSectionsSelector,
	makeGetNewBusinessApplicationProgressPercentageSelector,
	makeAreAllNewBusinessApplicationSectionsCompleteSelector,
	makeGetDefaultNewBusinessApplicationSectionSelector, 
	makeGetDefaultNewBusinessApplicationSectionNameSelector,
	makeGetNewBusinessApplicationSectionSelector,
	makeDoesNewBusinessApplicationSectionExistSelector,
	makeIsNewBusinessApplicationSectionRequiredSelector,
	makeIsNewBusinessApplicationSectionEligibleSelector,
	makeIsNewBusinessApplicationSectionCompleteSelector,
	makeIsNewBusinessApplicationSectionAccessibleSelector,
	makeGetNewBusinessApplicationSectionLabelSelector,
	makeGetNewBusinessApplicationSectionStatusMessageSelector,
	makeGetNewBusinessApplicationSectionNonEligibilityStatusMessageSelector,
	makeIsNewBusinessApplicationSectionEditableSelector,
	makeRequireUnsubmissionOnEditsForNewBusinessApplicationSectionSelector,
};